// Small pieces the three light screens share, all matching the Flutter styling.
//
//  ChannelSlider   the fat 30px-track slider from buildStyledSlider()
//  VerticalSlider  the WWH "water" slider — same idea, stood on end
//  PresetButton    a 70×70 rounded-12 swatch with a glow and a label under it
//  ApplyingOverlay the dimmed "Applying Changes..." veil shown after a command
//
// The overlay exists because these devices take a moment to act on a new colour;
// Flutter shows it for 2–3 s after every publish so a user doesn't tap twice.

import React from 'react';
import {
  Box, Slider, Typography, CircularProgress, ButtonBase, useTheme,
} from '@mui/material';

export function ChannelSlider({
  value, onChange, onCommit, activeColor,
}) {
  return (
    <Slider
      value={value}
      min={0}
      max={255}
      step={1}
      valueLabelDisplay="auto"
      onChange={(_, v) => onChange(v)}
      onChangeCommitted={() => onCommit()}
      sx={{
        color: activeColor,
        height: 30,
        '& .MuiSlider-track': { border: 'none', height: 30, borderRadius: '15px' },
        '& .MuiSlider-rail': { bgcolor: '#E0E0E0', opacity: 1, height: 30, borderRadius: '15px' },
        '& .MuiSlider-thumb': {
          width: 24,
          height: 24,
          bgcolor: '#fff',
          border: '2px solid currentColor',
          '&:hover, &.Mui-focusVisible': { boxShadow: '0 0 0 8px rgba(0,0,0,0.12)' },
        },
      }}
    />
  );
}

export function VerticalSlider({
  value, onChange, onCommit, activeColor, label, height = 240,
}) {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <Slider
        orientation="vertical"
        value={value}
        min={0}
        max={255}
        step={1}
        valueLabelDisplay="auto"
        onChange={(_, v) => onChange(v)}
        onChangeCommitted={() => onCommit()}
        sx={{
          color: activeColor,
          height,
          width: 30,
          '& .MuiSlider-track': { border: 'none', width: 30, borderRadius: '15px' },
          '& .MuiSlider-rail': { bgcolor: '#E0E0E0', opacity: 1, width: 30, borderRadius: '15px' },
          '& .MuiSlider-thumb': {
            width: 24,
            height: 24,
            bgcolor: '#fff',
            border: '2px solid currentColor',
          },
        }}
      />
      <Typography sx={{ color: theme.sli.text, fontSize: 14, fontWeight: 600, textAlign: 'center' }}>
        {label}
      </Typography>
    </Box>
  );
}

export function PresetButton({ label, swatch, onClick, size = 70 }) {
  const theme = useTheme();
  const c = theme.sli;
  // "Off" is a black square, so it gets a light glow to stay visible on a dark
  // background — the same trick the Dart uses (textFromAPI glow at 50%).
  const isDark = swatch === '#000000';
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25 }}>
      <ButtonBase
        onClick={onClick}
        sx={{
          width: size,
          height: size,
          borderRadius: '12px',
          bgcolor: swatch,
          boxShadow: isDark
            ? `0 2px 16px 1px ${c.text}80`
            : '0 2px 16px 4px rgba(0,0,0,0.5)',
        }}
      />
      <Typography sx={{
        color: c.text,
        // Scale with the swatch — at the WWRGB size (56) a 17px label overflows
        // its grid column, especially the two-line "Warm White".
        fontSize: size >= 70 ? 17 : 13,
        fontWeight: 700,
        textAlign: 'center',
        whiteSpace: 'pre-line',
        lineHeight: 1.15,
      }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export function ApplyingOverlay({ open }) {
  const theme = useTheme();
  if (!open) return null;
  return (
    <Box sx={{
      position: 'fixed',
      inset: 0,
      zIndex: 1400,
      bgcolor: 'rgba(0,0,0,0.4)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1.5,
    }}
    >
      <CircularProgress size={40} thickness={3} sx={{ color: theme.sli.button }} />
      <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'rgba(0,0,0,0.87)' }}>
        Applying Changes...
      </Typography>
    </Box>
  );
}
